import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Truck,
  Save,
  Loader2,
  CheckCircle2,
  Zap,
  Eye,
  EyeOff,
  Mail,
} from "lucide-react";

export default function HepsiJetSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [credentialsGateOpen, setCredentialsGateOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationChecking, setVerificationChecking] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [verifiedCredentials, setVerifiedCredentials] = useState(null);

  const [config, setConfig] = useState({
    username: "",
    password: "",
    company_name: "",
    abbreviation_code: "",
    sender_address_id: "",
    current_xdock: "",
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const data = await api.get("/hepsijet/config");
      if (data.configured) {
        setConfigured(true);
        setConfig((prev) => ({
          ...prev,
          company_name: data.company_name || "",
          abbreviation_code: data.abbreviation_code || "",
          sender_address_id: data.sender_address_id || "",
          current_xdock: data.current_xdock || "",
        }));
      }
    } catch (err) {
      console.error("Config fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const requestCredentialsCode = async () => {
    setVerificationSending(true);
    try {
      const res = await api.post("/hepsijet/credentials/access/request");
      toast.success(res?.message || "Doğrulama kodu gönderildi.");
    } catch (error) {
      toast.error(error.message || "Kod gönderilemedi.");
    } finally {
      setVerificationSending(false);
    }
  };

  const handleVerifyCredentialsCode = async () => {
    const code = String(verificationCode || "").trim();
    if (!code) {
      toast.error("Lütfen doğrulama kodunu girin.");
      return;
    }
    setVerificationChecking(true);
    try {
      const verify = await api.post("/hepsijet/credentials/access/verify", { code });
      const token = verify?.access_token;
      if (!token) throw new Error("Erişim doğrulaması alınamadı.");
      const detail = await api.get(`/hepsijet/credentials/secret?access_token=${encodeURIComponent(token)}`);
      setVerifiedCredentials(detail?.credentials || null);
      setCredentialsGateOpen(false);
      setCredentialsModalOpen(true);
      setVerificationCode("");
    } catch (error) {
      toast.error(error.message || "Doğrulama başarısız.");
    } finally {
      setVerificationChecking(false);
    }
  };

  const handleSave = async () => {
    if (!config.username || !config.password || !config.company_name || !config.abbreviation_code) {
      toast.error("Kullanıcı adı, şifre, firma adı ve kısaltma kodu zorunludur");
      return;
    }
    setSaving(true);
    try {
      const result = await api.post("/hepsijet/save-config", config);
      if (result.success) {
        toast.success("HepsiJet ayarları kaydedildi");
        setConfigured(true);
      }
    } catch (err) {
      toast.error(err.message || "Kaydetme hatası");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await api.post("/hepsijet/test");
      if (result.success) {
        toast.success(result.message || "Bağlantı başarılı!");
      } else {
        toast.error(result.message || "Bağlantı hatası");
      }
    } catch (err) {
      toast.error(err.message || "Bağlantı test edilemedi");
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    setSaving(true);
    try {
      const result = await api.post("/hepsijet/disconnect");
      if (result.success) {
        toast.success("HepsiJet bağlantısı kaldırıldı");
        setConfigured(false);
        setConfig({
          username: "",
          password: "",
          company_name: "",
          abbreviation_code: "",
          sender_address_id: "",
          current_xdock: "",
        });
      } else {
        toast.error(result.message || "Bağlantı kaldırılamadı");
      }
    } catch (err) {
      toast.error(err.message || "Bağlantı kaldırılamadı");
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const closePanel = () => navigate("/panel/entegrasyonlar");

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div>
      <Sheet open onOpenChange={(open) => (!open ? closePanel() : null)}>
        <SheetContent className="fixed right-0 top-0 z-[400] h-screen w-full max-w-full sm:max-w-[680px] p-0 overflow-y-auto">
          <SheetHeader className="px-4 sm:px-5 py-4 border-b border-border bg-card">
            <SheetTitle className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={closePanel} className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="w-9 h-9 rounded-lg border bg-card flex items-center justify-center overflow-hidden">
                <Truck className="h-5 w-5 text-orange-600" />
              </div>
              <div className="text-left">
                <div className="text-lg font-semibold leading-tight">HepsiJet Entegrasyonu</div>
                <div className="text-xs text-muted-foreground">Kargo entegrasyon ayarları</div>
              </div>
              {configured ? (
                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Bağlı
                </span>
              ) : null}
            </SheetTitle>
            <SheetDescription>Genel ve güvenlik ayarlarını buradan yönetin.</SheetDescription>
          </SheetHeader>

          <div className="px-4 sm:px-5 py-4 space-y-4">
            <div className="surface-card overflow-hidden">
              <div className="sheet-heading">
                <div>
                  <h2>API Kimlik Bilgileri</h2>
                  <p>{configured ? "Bağlantı aktif. Güvenlik nedeniyle API bilgileri maskelenmiştir." : "HepsiJet API erişim bilgilerinizi girin."}</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {configured ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm text-emerald-800">HepsiJet bağlantısı başarılı.</div>
                      <Button
                        variant="outline"
                        className="shrink-0"
                        onClick={async () => {
                          setCredentialsGateOpen(true);
                          setVerificationCode("");
                          setVerifiedCredentials(null);
                          await requestCredentialsCode();
                        }}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        API Bilgileri
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>Kullanıcı Adı (Username)</Label>
                      <Input value={config.username} onChange={(e) => updateConfig("username", e.target.value)} placeholder="ornek_integration" data-testid="hj-username" />
                    </div>
                    <div>
                      <Label>Şifre (Password)</Label>
                      <div className="relative">
                        <Input type={showPassword ? "text" : "password"} value={config.password} onChange={(e) => updateConfig("password", e.target.value)} placeholder="********" className="pr-10" data-testid="hj-password" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="surface-card overflow-hidden">
              <div className="sheet-heading">
                <div>
                  <h2>Firma Bilgileri</h2>
                  <p>HepsiJet tarafında tanımlanan sabit şirket parametreleri</p>
                </div>
              </div>
              <div className="p-5 space-y-4">
                {configured ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-sm text-emerald-800">
                        Firma bilgileri maskelendi. API Bilgileri ile doğrulama sonrası görüntüleyebilirsiniz.
                      </div>
                      <Button
                        variant="outline"
                        className="shrink-0"
                        onClick={async () => {
                          setCredentialsGateOpen(true);
                          setVerificationCode("");
                          setVerifiedCredentials(null);
                          await requestCredentialsCode();
                        }}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        API Bilgileri
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label>Firma Adı (Company Name)</Label>
                      <Input value={config.company_name} onChange={(e) => updateConfig("company_name", e.target.value)} placeholder="Örn: Acme Ticaret" />
                    </div>
                    <div>
                      <Label>Kısaltma Kodu (Abbreviation Code)</Label>
                      <Input value={config.abbreviation_code} onChange={(e) => updateConfig("abbreviation_code", e.target.value)} placeholder="Örn: ACM" />
                    </div>
                    <div>
                      <Label>Firma Adres Kodu (Company Address ID)</Label>
                      <Input value={config.sender_address_id} onChange={(e) => updateConfig("sender_address_id", e.target.value)} placeholder="fiki-fikirizm-539" data-testid="hj-address-id" />
                    </div>
                    <div>
                      <Label>Firma Depo Kodu (CurrentXDock)</Label>
                      <Input value={config.current_xdock} onChange={(e) => updateConfig("current_xdock", e.target.value)} placeholder="FIKIRIZMNARLIDEREUser" data-testid="hj-xdock" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 border-t border-border bg-card px-4 sm:px-5 py-3 flex flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="hj-test-btn">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
              Bağlantıyı Test Et
            </Button>
            {!configured ? (
              <Button onClick={handleSave} disabled={saving} data-testid="hj-save-btn">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Kaydet
              </Button>
            ) : (
              <Button variant="destructive" onClick={handleDisconnect} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Bağlantıyı Kaldır
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={credentialsGateOpen} onOpenChange={setCredentialsGateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>E-posta Doğrulaması</DialogTitle>
            <DialogDescription>
              HepsiJet bilgilerini görmek için e-posta adresinize gelen doğrulama kodunu girin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Doğrulama Kodu</Label>
            <Input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} placeholder="6 haneli kod" />
            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={requestCredentialsCode} disabled={verificationSending}>
                {verificationSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Kodu Yeniden Gönder
              </Button>
              <Button onClick={handleVerifyCredentialsCode} disabled={verificationChecking}>
                {verificationChecking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Doğrula
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={credentialsModalOpen} onOpenChange={setCredentialsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>HepsiJet API Bilgileri</DialogTitle>
            <DialogDescription>Doğrulama başarılı. Bilgileri güvenli şekilde görüntülüyorsunuz.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Kullanıcı Adı</Label>
              <Input value={verifiedCredentials?.username || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Şifre</Label>
              <Input value={verifiedCredentials?.password || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Firma Adı</Label>
              <Input value={verifiedCredentials?.company_name || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Kısaltma Kodu</Label>
              <Input value={verifiedCredentials?.abbreviation_code || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Firma Adres Kodu</Label>
              <Input value={verifiedCredentials?.sender_address_id || "-"} readOnly />
            </div>
            <div className="space-y-1">
              <Label>Firma Depo Kodu</Label>
              <Input value={verifiedCredentials?.current_xdock || "-"} readOnly />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
